import { config as appConfig } from 'config';
import { IncomingMessage, ServerResponse } from 'http';
import { ParsedUrlQuery } from 'querystring';
import { GGTheme } from 'themes/types';
import parser from 'ua-parser-js';

import { CoreFeaturesBackendData } from '@glossgenius/core-features-react-sdk';

import type { Membership } from 'types/membership';
import type { Package } from 'types/package';
import type { TPublicUser } from 'types/publicUser';

import configureStore from '../store/configureStore';
import { getSalonConfiguration } from './getSalonConfiguration';
import { getSiteFromReq } from './getSiteFromReq';
import { initializeHttpClient } from './httpClient/httpClient.config';
import routerError from './routerError';

// Initialize the HTTP Client that will be used server side
initializeHttpClient();

export async function getCommonServersideProps(
  req: IncomingMessage,
  res: ServerResponse,
  query: ParsedUrlQuery,
) {
  const site = getSiteFromReq(req, res, query);

  try {
    const {
      publicUser,
      packages,
      memberships,
      theme,
      coreFeaturesBackendData,
    } = await getSalonConfiguration(site);

    const browser = req ? parser(req.headers['user-agent']).browser : undefined;

    return {
      browser,
      publicUser,
      packages,
      memberships,
      theme,
      coreFeaturesBackendData,
    };
  } catch (error: any) {
    throw routerError({
      status: 404,
      name: 'SlugNotFound',
      message: error,
    });
  }
}

/**
 * This function takes the serialized data that makes up common
 * serverside props and hydrates them. This is required to turn
 * the redux store data into an actual redux store, which is
 * not serializable.
 *
 * @param props props that should contain `browser` and `publicUser`
 */
export function hydrateCommonServersideProps(
  props:
    | {
        browser: string | undefined;
        publicUser: TPublicUser;
        packages: Package[];
        memberships: Membership[];
        theme?: GGTheme;
        coreFeaturesBackendData: CoreFeaturesBackendData;
      }
    | undefined,
) {
  if (typeof window !== 'undefined' && window.context) {
    return window.context;
  }

  const config = {
    apiUrlV2: appConfig.apiUrlV2,
    apiUrlV3: appConfig.apiUrlV3,
    apiUrlV4: appConfig.apiUrlV4,
    rootUrl: appConfig.rootUrl,
    browser: props?.browser,
  };
  const store = configureStore({
    location: { path: '/' },
    publicUser: props?.publicUser,
    bookableAppointments: {
      serviceProviders: props?.publicUser?.users,
      appointments: [],
    },
  });

  const context = {
    store,
    config,
    publicUser: props?.publicUser,
    packages: props?.packages,
    memberships: props?.memberships,
    theme: props?.theme,
    coreFeaturesBackendData: props?.coreFeaturesBackendData,
  };

  if (typeof window !== 'undefined') {
    window.context = context;
  }

  return context;
}
